"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { AstroMascot } from "./AstroMascot";
import { 
  ArrowRight,
  Mail,
  Rocket,
  Clock,
  Star
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface EmailTemplateProps {
  type: 'candidate_incomplete' | 'hr_review_pending';
  candidateName?: string;
  currentStep?: string;
  onPreviewClick?: () => void;
}

export function EmailTemplate({ 
  type, 
  candidateName = "Sarah",
  currentStep = "Document Review",
  onPreviewClick 
}: EmailTemplateProps) {
  
  if (type === 'candidate_incomplete') {
    return (
      <Card className="max-w-2xl mx-auto bg-white shadow-lg border border-gray-200 overflow-hidden">
        {/* Email Header */}
        <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto brightness-0 invert"
            />
            <div className="text-right">
              <p className="text-sm opacity-90">DayOne Onboarding</p>
              <p className="text-xs opacity-75">Your journey continues</p>
            </div>
          </div>
        </div>

        {/* Email Content */}
        <div className="p-8">
          {/* Subject Line Preview */}
          <div className="mb-6 p-3 bg-gray-50 rounded-lg border-l-4 border-purple-500">
            <p className="text-xs text-gray-600 mb-1">Subject:</p>
            <p className="text-gray-900">Still with us, {candidateName}?</p>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Greeting with Astro */}
            <div className="flex items-start space-x-4">
              <motion.div
                animate={{ 
                  y: [0, -5, 0],
                  rotate: [0, 2, -2, 0]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <AstroMascot expression="waving" size="lg" />
              </motion.div>
              
              <div className="flex-1">
                <h2 className="text-gray-900 mb-3">
                  Hi {candidateName}! 👋
                </h2>
                <div className="space-y-3 text-gray-700">
                  <p>
                    Astro here! I noticed your onboarding mission is still in progress, 
                    and I wanted to check in with you.
                  </p>
                  <p>
                    We're excited to have you join the OpenTurf family, and I'm here to 
                    help make your journey as smooth as possible. You were last working 
                    on <strong>{currentStep}</strong> — let's get you back on track!
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-sm">Your Progress</h3>
                  <p className="text-gray-600 text-xs">Pick up where you left off</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Current Step</span>
                  <span className="text-blue-700">{currentStep}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full w-3/5"></div>
                </div>
                <p className="text-xs text-gray-500">60% complete</p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center">
              <Button
                onClick={onPreviewClick}
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-8 py-3 mb-4"
              >
                <Rocket className="w-5 h-5 mr-2" />
                Continue My Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              
              <p className="text-xs text-gray-500">
                This link will take you directly to your onboarding dashboard
              </p>
            </div>

            {/* Motivational Message */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-gray-900 text-sm">Astro's Tip</span>
              </div>
              <p className="text-gray-700 text-sm">
                The entire process takes less than 10 minutes. Once you're done, 
                you'll have access to your welcome dashboard with all the resources 
                you need for your first day!
              </p>
            </div>

            {/* Footer Message */}
            <div className="text-center text-gray-600 text-sm">
              <p>
                Need help? Just reply to this email and our team will assist you.
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Welcome to the OpenTurf journey! 🚀
              </p>
            </div>
          </div>
        </div>

        {/* Email Footer */}
        <div className="bg-gray-50 p-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-2">
              <img 
                src={openTurfLogo} 
                alt="OpenTurf Technologies" 
                className="h-4 w-auto opacity-60"
              />
              <span>© 2024 OpenTurf Technologies</span>
            </div>
            <div className="flex items-center space-x-3">
              <a href="#" className="text-blue-600 hover:underline">Unsubscribe</a>
              <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // HR Review Pending Email
  return (
    <Card className="max-w-2xl mx-auto bg-white shadow-lg border border-gray-200 overflow-hidden">
      {/* Email Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 p-6 text-white">
        <div className="flex items-center justify-between">
          <img 
            src={openTurfLogo} 
            alt="OpenTurf Technologies" 
            className="h-8 w-auto brightness-0 invert"
          />
          <div className="text-right">
            <p className="text-sm opacity-90">HR Portal</p>
            <p className="text-xs opacity-75">Candidate Review Needed</p>
          </div>
        </div>
      </div>

      {/* Email Content */}
      <div className="p-8">
        {/* Subject Line Preview */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg border-l-4 border-orange-500">
          <p className="text-xs text-gray-600 mb-1">Subject:</p>
          <p className="text-gray-900">You've got a DayOne submission waiting</p>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          <div>
            <h2 className="text-gray-900 mb-3">
              Review Required 📋
            </h2>
            <div className="space-y-3 text-gray-700">
              <p>
                A candidate has completed their DayOne onboarding submission and 
                is waiting for your review.
              </p>
            </div>
          </div>

          {/* Candidate Details */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <h3 className="text-gray-900 mb-4">Candidate Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span>
                <span className="text-gray-900 ml-2">{candidateName} Johnson</span>
              </div>
              <div>
                <span className="text-gray-600">Role:</span>
                <span className="text-gray-900 ml-2">Senior Developer</span>
              </div>
              <div>
                <span className="text-gray-600">Department:</span>
                <span className="text-gray-900 ml-2">Engineering</span>
              </div>
              <div>
                <span className="text-gray-600">Submitted:</span>
                <span className="text-gray-900 ml-2">2 days ago</span>
              </div>
            </div>
          </div>

          {/* Urgency Notice */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-4 h-4 text-red-600" />
              <span className="text-red-900 text-sm">Time Sensitive</span>
            </div>
            <p className="text-red-700 text-sm">
              This submission has been pending for 48+ hours. Please review to keep 
              the onboarding process on track.
            </p>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <Button
              onClick={onPreviewClick}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-3 mb-4"
            >
              <Mail className="w-5 h-5 mr-2" />
              Review Submission
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <p className="text-xs text-gray-500">
              Access the HR dashboard to review documents and approve onboarding
            </p>
          </div>
        </div>
      </div>

      {/* Email Footer */}
      <div className="bg-gray-50 p-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-4 w-auto opacity-60"
            />
            <span>© 2024 OpenTurf Technologies</span>
          </div>
          <div className="flex items-center space-x-3">
            <a href="#" className="text-blue-600 hover:underline">HR Settings</a>
            <a href="#" className="text-blue-600 hover:underline">Support</a>
          </div>
        </div>
      </div>
    </Card>
  );
}